import { useMemo } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { getPercentInRange } from "@core/helpers";
import { TopPlacesDetails } from "@apiHooks/useHomeTopChainsByNPS";
import "./TopChainsChart.styles.scss";

interface TopChainsChartProps {
    data: TopPlacesDetails;
}

const BARS_GAP = 16;
const ANIMATION_DELAY_PER_BAR = 150;

const TopChainsChart: FCX<TopChainsChartProps> = ({ data }) => {
    const { name, topChains = [], averageNPS = 0 } = data;
    const chains = topChains || [];
    
    const { minNPS, npsRange, averageLinePosition, barWidth } = useMemo(() => {
        const width = chains.length > 0 
            ? `calc((100% - ${(chains.length - 1) * BARS_GAP}px) / ${chains.length})` 
            : '100%';
            
        if (chains.length === 0) {
            return { maxNPS: 0, minNPS: 0, npsRange: 0, averageLinePosition: null, barWidth: width };
        }
        
        const npsValues = chains.map(chain => chain.nps || 0);
        const max = Math.max(...npsValues, averageNPS);
        const min = Math.min(...npsValues, averageNPS, 0);
        const range = max - min;
        
        return { 
            maxNPS: max, 
            minNPS: min, 
            npsRange: range, 
            averageLinePosition: range > 0 ? getPercentInRange(averageNPS, min, max) : null,
            barWidth: width
        };
    }, [chains, averageNPS]);
    
    const lineAnimationDuration = chains.length * ANIMATION_DELAY_PER_BAR;
    
    return (
        <div className="TopChainsChart">
            <div className="TopChainsChart__bars" style={{ gap: BARS_GAP }}>
                {averageLinePosition !== null && (
                    <div
                        key={name + "-average-line"}
                        className="TopChainsChart__average-line"
                        style={{ bottom: `${averageLinePosition}%`, animationDuration: `${lineAnimationDuration}ms` }}
                    >
                        <span
                            className="TopChainsChart__average-label appears"
                            style={{ animationDelay: `${lineAnimationDuration}ms` }}
                        >
                            {averageNPS > 0 ? `+${averageNPS}` : averageNPS}
                        </span>
                    </div>
                )}
                {chains.map((chain, index) => {
                    const npsValue = chain.nps || 0;
                    const heightPercent = npsRange > 0 ? getPercentInRange(npsValue, minNPS, minNPS + npsRange) : 0;
                    const barHeight = Math.max(heightPercent, 3);
                    
                    return (
                        <div
                            key={`${name}-bar-${chain.id || index}`}
                            className={`TopChainsChart__bar appears-top del-${index}`}
                            style={{ width: barWidth, height: `${barHeight}%` }}
                        >
                            <div className="TopChainsChart__bar-value">
                                {npsValue > 0 ? `+${npsValue}` : npsValue}
                            </div>
                        </div>
                    );
                })}
            </div>
            
            <div className="TopChainsChart__names-row" style={{ gap: BARS_GAP }}>
                {chains.map((chain, index) => (
                    <div
                        key={`${name}-name-${chain.id || index}`}
                        className={`TopChainsChart__bar-name appears-top del-${index}`}
                        style={{ width: barWidth }}
                    >
                        {chain.name}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default TopChainsChart;